import { useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/providers/AuthProvider";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Loader2, X } from "lucide-react";
import { toast } from "@/components/ui/use-toast";
import { useTranslation } from "react-i18next";
import { useKanbanColumns } from "@/hooks/useKanbanColumns";

type EntityType = "jobs" | "candidates" | "matches";

interface EmailTemplate {
  id: string;
  name: string;
  subject: string;
}

interface TransitionTemplateRow {
  id?: string;
  entity_type: EntityType;
  from_status: string | null;
  to_status: string;
  template_id: string;
}

const entityLabels: Record<EntityType, string> = {
  jobs: "Jobs",
  candidates: "Candidates",
  matches: "Matches",
};

const STATUS_ORDER: Record<EntityType, string[]> = {
  matches: ["applied", "matched", "screening", "sent", "interview", "offer", "hired", "application_closed", "candidate_not_available", "candidate_not_interested", "offer_rejected_by_candidate", "rejected_by_customer", "rejected_by_recruter"],
  candidates: ["New", "Looking", "Open", "Unavailable", "hired"],
  jobs: ["open", "hold", "closed/won", "closed/lost"],
};

function sortStatuses(entity: EntityType, statuses: string[]): string[] {
  const preferred = STATUS_ORDER[entity] || [];
  const priority = new Map(preferred.map((s, i) => [s.toLowerCase(), i] as const));
  return [...statuses].sort((a, b) => {
    const pa = priority.get(a.toLowerCase());
    const pb = priority.get(b.toLowerCase());
    if (pa !== undefined && pb !== undefined) return pa - pb;
    if (pa !== undefined) return -1;
    if (pb !== undefined) return 1;
    return a.localeCompare(b);
  });
}

const STATUS_COLOR: Record<string, string> = {
  matched: "sky", applied: "blue", screening: "amber", interview: "orange",
  offer: "violet", sent: "purple", hired: "emerald",
  application_closed: "red", candidate_not_available: "stone", candidate_not_interested: "rose",
  offer_rejected_by_candidate: "pink", rejected_by_customer: "orange", rejected_by_recruter: "amber",
  new: "cyan", looking: "teal", open: "green", unavailable: "stone",
  "hold": "yellow", "closed/won": "emerald", "closed/lost": "pink",
};

function statusHue(status: string): string {
  return STATUS_COLOR[status.trim().toLowerCase()] || "slate";
}

const COLOR_CLASSES: Record<string, { badge: string; section: string }> = {
  sky: { badge: "bg-sky-100 text-sky-700", section: "border-sky-200 bg-sky-50" },
  blue: { badge: "bg-blue-100 text-blue-700", section: "border-blue-200 bg-blue-50" },
  amber: { badge: "bg-amber-100 text-amber-700", section: "border-amber-200 bg-amber-50" },
  orange: { badge: "bg-orange-100 text-orange-700", section: "border-orange-200 bg-orange-50" },
  violet: { badge: "bg-violet-100 text-violet-700", section: "border-violet-200 bg-violet-50" },
  purple: { badge: "bg-purple-100 text-purple-700", section: "border-purple-200 bg-purple-50" },
  emerald: { badge: "bg-emerald-100 text-emerald-700", section: "border-emerald-200 bg-emerald-50" },
  rose: { badge: "bg-rose-100 text-rose-700", section: "border-rose-200 bg-rose-50" },
  cyan: { badge: "bg-cyan-100 text-cyan-700", section: "border-cyan-200 bg-cyan-50" },
  teal: { badge: "bg-teal-100 text-teal-700", section: "border-teal-200 bg-teal-50" },
  green: { badge: "bg-green-100 text-green-700", section: "border-green-200 bg-green-50" },
  stone: { badge: "bg-stone-100 text-stone-700", section: "border-stone-200 bg-stone-50" },
  yellow: { badge: "bg-yellow-100 text-yellow-700", section: "border-yellow-200 bg-yellow-50" },
  red: { badge: "bg-red-100 text-red-700", section: "border-red-200 bg-red-50" },
  pink: { badge: "bg-pink-100 text-pink-700", section: "border-pink-200 bg-pink-50" },
  slate: { badge: "bg-slate-100 text-slate-700", section: "border-slate-200 bg-slate-50" },
};

function getColorClasses(hue: string) {
  return COLOR_CLASSES[hue] || COLOR_CLASSES.slate;
}

async function fetchDistinctStatuses(entity: EntityType): Promise<string[]> {
  // Start with all known statuses from STATUS_ORDER (canonical casing)
  const preferred = STATUS_ORDER[entity] || [];
  const seen = new Map<string, string>(); // lowercase -> canonical
  for (const s of preferred)
    seen.set(s.toLowerCase(), s);
  // Merge in any additional statuses from the database
  const { data, error } = await supabase
    .from(entity)
    .select("status")
    .limit(2000);
  if (!error && data) {
    for (const row of data as any[]) {
      const s = (row?.status ?? "").toString().trim();
      if (s.length > 0 && !seen.has(s.toLowerCase()))
        seen.set(s.toLowerCase(), s);
    }
  }
  return Array.from(seen.values());
}

async function fetchTemplates(orgId?: string | null): Promise<EmailTemplate[]> {
  let q = supabase.from("email_templates").select("id,name,subject_en");
  if (orgId) q = q.eq("organization_uuid", orgId);
  const { data, error } = await q.order("name", { ascending: true });
  if (error) throw error;
  return (data || []).map((row: any) => ({ id: row.id, name: row.name, subject: row.subject_en || "" })) as EmailTemplate[];
}

async function fetchExistingMappings(orgId: string | null): Promise<TransitionTemplateRow[]> {
  let q = supabase
    .from("transition_templates")
    .select("id, entity_type, from_status, to_status, template_id");
  if (orgId) q = q.eq("organization_uuid", orgId);
  const { data, error } = await q;
  if (error) throw error;
  return (data || []) as TransitionTemplateRow[];
}

async function upsertMapping(row: TransitionTemplateRow, orgId: string | null) {
  const payload: any = {
    entity_type: row.entity_type,
    from_status: row.from_status,
    to_status: row.to_status,
    template_id: row.template_id,
  };
  if (orgId) payload.organization_uuid = orgId;
  const { error } = await supabase.from("transition_templates").upsert(payload, {
    onConflict: "organization_uuid,entity_type,from_status,to_status",
  });
  if (error) throw error;
}

export default function TransitionTemplatesTab() {
  const { t } = useTranslation();
  const { currentOrganization } = useAuth();
  const orgId = currentOrganization?.id ?? null;

  const [loading, setLoading] = useState(true);
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [statusMap, setStatusMap] = useState<Record<EntityType, string[]>>({
    jobs: [], candidates: [], matches: [],
  });
  const [selection, setSelection] = useState<Record<string, string>>({});

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const [jobs, candidates, matches, tmpl, existing] = await Promise.all([
          fetchDistinctStatuses("jobs"),
          fetchDistinctStatuses("candidates"),
          fetchDistinctStatuses("matches"),
          fetchTemplates(orgId),
          fetchExistingMappings(orgId),
        ]);
        if (cancelled) return;
        setStatusMap({ jobs, candidates, matches });
        setTemplates(tmpl);
        const map: Record<string, string> = {};
        for (const row of existing) {
          const key = `${row.entity_type}|${row.from_status ?? ""}|${row.to_status}`;
          map[key] = row.template_id;
        }
        setSelection(map);
      } catch (e: any) {
        toast({ title: "Error", description: e.message, variant: "destructive" });
      } finally {
        setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [orgId]);

  const entityTabs = useMemo<EntityType[]>(() => ["matches", "candidates", "jobs"], []);

  const { columns: kanbanColumnDefs } = useKanbanColumns();

  const sortedStatusMap = useMemo(() => {
    // Include org-defined custom kanban columns even before any match reaches them
    const known = new Set(statusMap.matches.map((s) => s.toLowerCase()));
    const matchesWithCustom = [
      ...statusMap.matches,
      ...kanbanColumnDefs.filter((c) => !c.isSystem && !known.has(c.id.toLowerCase())).map((c) => c.id),
    ];
    return {
      jobs: sortStatuses("jobs", statusMap.jobs),
      candidates: sortStatuses("candidates", statusMap.candidates),
      matches: sortStatuses("matches", matchesWithCustom),
    } as Record<EntityType, string[]>;
  }, [statusMap, kanbanColumnDefs]);

  const handleClear = async (entity: EntityType, fromStatus: string | null, toStatus: string) => {
    const key = `${entity}|${fromStatus ?? ""}|${toStatus}`;
    setSelection((s) => {
      const next = { ...s };
      delete next[key];
      return next;
    });
    try {
      let q = supabase.from("transition_templates").delete().eq("entity_type", entity).eq("to_status", toStatus);
      if (orgId) q = q.eq("organization_uuid", orgId);
      if (fromStatus) q = q.eq("from_status", fromStatus);
      else q = q.is("from_status", null);
      await q;
      toast({ title: "Removed", description: `Template for ${fromStatus ?? "Any"} → ${toStatus} cleared`, duration: 2000 });
    } catch (e: any) {
      toast({ title: "Error", description: e.message, variant: "destructive" });
    }
  };

  const handleChange = async (
    entity: EntityType,
    fromStatus: string | null,
    toStatus: string,
    templateId: string
  ) => {
    const key = `${entity}|${fromStatus ?? ""}|${toStatus}`;
    setSelection((s) => ({ ...s, [key]: templateId }));
    try {
      // Delete + insert instead of upsert (NULL from_status breaks onConflict)
      let q = supabase.from("transition_templates").delete().eq("entity_type", entity).eq("to_status", toStatus);
      if (orgId) q = q.eq("organization_uuid", orgId);
      if (fromStatus) q = q.eq("from_status", fromStatus);
      else q = q.is("from_status", null);
      await q;

      const payload: any = { entity_type: entity, from_status: fromStatus, to_status: toStatus, template_id: templateId };
      if (orgId) payload.organization_uuid = orgId;
      const { error } = await supabase.from("transition_templates").insert(payload);
      if (error) throw error;

      toast({
        title: "Saved",
        description: `Template for ${fromStatus ?? "Any"} → ${toStatus} updated`,
        duration: 2000,
      });
    } catch (e: any) {
      toast({ title: "Error", description: e.message, variant: "destructive" });
    }
  };

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        {t("admin.transitionTemplates.description", "Map status changes to email templates. Changes save automatically.")}
      </p>
      <Tabs defaultValue="matches">
        <TabsList>
          {entityTabs.map((e) => (
            <TabsTrigger key={e} value={e}>
              {entityLabels[e]}
            </TabsTrigger>
          ))}
        </TabsList>
        {entityTabs.map((entity) => (
          <TabsContent key={entity} value={entity}>
            <Card>
              <CardHeader>
                <CardTitle>{entityLabels[entity]} transitions</CardTitle>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="flex items-center text-sm text-muted-foreground">
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading statuses and templates...
                  </div>
                ) : statusMap[entity].length === 0 ? (
                  <div className="text-sm text-muted-foreground">No statuses found.</div>
                ) : (
                  <div className="space-y-4">
                    {sortedStatusMap[entity].map((to) => {
                      const hue = statusHue(to);
                      const colorClasses = getColorClasses(hue);
                      return (
                      <div key={to} className={`rounded-sm border p-3 ${colorClasses.section}`}>
                        <div className="mb-3 flex items-center justify-between">
                          <div className="text-sm font-semibold">
                            To: <span className={`inline-flex items-center rounded px-1.5 py-0.5 text-xs ml-1 ${colorClasses.badge}`}>{to}</span>
                          </div>
                          <div className="text-xs text-muted-foreground">Select templates for transitions ending in "{to}"</div>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                          {[null, ...sortedStatusMap[entity].filter((s) => s !== to)].map((from) => {
                            const key = `${entity}|${from ?? ""}|${to}`;
                            return (
                              <div key={key} className="flex items-center gap-3">
                                <div className="min-w-0 flex-1">
                                  <div className="text-sm font-medium truncate">
                                    <span className={`inline-flex items-center rounded px-1.5 py-0.5 text-xs mr-1 ${colorClasses.badge}`}>
                                      {from ?? "Any"}
                                    </span>
                                    →
                                    <span className={`inline-flex items-center rounded px-1.5 py-0.5 text-xs ml-1 ${colorClasses.badge}`}>
                                      {to}
                                    </span>
                                  </div>
                                  <div className="text-xs text-muted-foreground truncate">Template</div>
                                </div>
                                <div className="flex items-center gap-1">
                                  <div className="w-52">
                                    <Select
                                      value={selection[key] || ""}
                                      onValueChange={(v) => handleChange(entity, from, to, v)}
                                    >
                                      <SelectTrigger>
                                        <SelectValue placeholder="None" />
                                      </SelectTrigger>
                                      <SelectContent>
                                        {templates.map((tmpl) => (
                                          <SelectItem key={tmpl.id} value={tmpl.id}>
                                            {tmpl.name}
                                          </SelectItem>
                                        ))}
                                      </SelectContent>
                                    </Select>
                                  </div>
                                  {selection[key] && (
                                    <button
                                      type="button"
                                      onClick={() => handleClear(entity, from, to)}
                                      className="shrink-0 rounded-sm p-1 text-muted-foreground hover:text-destructive hover:bg-destructive/10 border"
                                      title="Remove template"
                                    >
                                      <X className="h-3 w-3" />
                                    </button>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );})}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
}
